import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  CheckCheck,
  Download,
  ExternalLink,
  ImagePlus,
  Link2,
  Loader2,
  MoreVertical,
  PanelLeft,
  Send,
  ShieldBan,
  Trash2,
  UserMinus,
  Users,
  X,
} from "lucide-react";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@eduviaos/ui-kit";
import { ResponsiveSheetContent } from "../../components/ResponsiveSheetContent";
import {
  useBanStudyGroupMember,
  useDeleteStudyGroupMessage,
  useDemoteStudyGroupModerator,
  usePostStudyGroupImageMessage,
  usePostStudyGroupMessage,
  usePromoteStudyGroupModerator,
  useRemoveStudyGroupMember,
  useSetStudyGroupChatProfile,
  useStudyGroupChatProfile,
  useStudyGroupMemberStats,
  useStudyGroupMembers,
  useStudyGroupMessageReceipts,
  useStudyGroupMessages,
  useUpdateStudyGroupInviteSettings,
} from "../../lib/learning/hooks";
import { coachApi } from "../../lib/learning/api";
import { putFileToSignedUrl } from "../../lib/profile/api";
import { useSession } from "../../lib/sessionContext";
import {
  markDeliveredMessagesRead,
  mergeStudyGroupMessageInCache,
  patchStudyGroupMessagesCache,
  useStudyGroupRealtime,
} from "../../lib/learning/studyGroupRealtime";
import {
  STUDY_GROUP_MENTION_ALL,
  type StudyGroup,
  type StudyGroupMember,
  type StudyGroupMessage,
} from "../../lib/learning/types";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import { intlLocale } from "../../lib/i18n/intl";
import type { PortalLocale } from "../../lib/i18n/portalShell";

/** Message refresh while the socket is down or only reaches one server worker. */
const CHAT_FALLBACK_POLL_MS = 4000;

function formatTime(iso: string, locale: PortalLocale): string {
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

function newClientMessageId(): string {
  return crypto.randomUUID();
}

function memberMatchesMentionQuery(member: StudyGroupMember, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (member.display_name.toLowerCase().includes(q)) return true;
  if (member.chat_username?.toLowerCase().includes(q)) return true;
  return false;
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const q = query.toLowerCase();
  const idx = text.toLowerCase().indexOf(q);
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/25 text-foreground rounded-sm px-0.5 not-italic">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function MemberIdentity({
  member,
  nameClassName = "text-base font-bold",
}: {
  member: Pick<StudyGroupMember, "display_name" | "chat_username">;
  nameClassName?: string;
}) {
  return (
    <span className="min-w-0 flex-1">
      <span className={cn("block truncate", nameClassName)}>{member.display_name}</span>
      {member.chat_username ? (
        <span className="text-muted-foreground block truncate text-xs">
          @{member.chat_username}
        </span>
      ) : null}
    </span>
  );
}

/** Full-viewport or embedded chat (SC-14c → SC-14e). */
export function StudyGroupChatRoom({
  group,
  onClose,
  layout = "fullscreen",
  listCollapsed = false,
  onToggleList,
}: {
  group: StudyGroup;
  onClose: () => void;
  layout?: "fullscreen" | "embedded";
  listCollapsed?: boolean;
  onToggleList?: () => void;
}) {
  const { user } = useSession();
  const { strings: s, locale } = usePortalLocale();
  const qc = useQueryClient();
  const profileQuery = useStudyGroupChatProfile(group.id);
  const setProfileMut = useSetStudyGroupChatProfile();
  const chatUsername = profileQuery.data?.chat_username ?? null;
  const chatReady = Boolean(chatUsername);
  const { connected, localOnly, typingUsers, wsRef } = useStudyGroupRealtime(group.id, chatReady);
  const messagesQuery = useStudyGroupMessages(
    group.id,
    chatReady,
    !connected || localOnly ? CHAT_FALLBACK_POLL_MS : false,
  );
  const membersQuery = useStudyGroupMembers(group.id, chatReady);
  const postMut = usePostStudyGroupMessage();
  const postImageMut = usePostStudyGroupImageMessage();
  const banMut = useBanStudyGroupMember();
  const removeMut = useRemoveStudyGroupMember();
  const inviteSettingsMut = useUpdateStudyGroupInviteSettings();
  const deleteMut = useDeleteStudyGroupMessage();
  const promoteMut = usePromoteStudyGroupModerator();
  const demoteMut = useDemoteStudyGroupModerator();

  const [draft, setDraft] = React.useState("");
  const [mentionIds, setMentionIds] = React.useState<string[]>([]);
  const [mentionQuery, setMentionQuery] = React.useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [membersOpen, setMembersOpen] = React.useState(false);
  const [statsMember, setStatsMember] = React.useState<StudyGroupMember | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [lightboxUrl, setLightboxUrl] = React.useState<string | null>(null);
  const [readByMessage, setReadByMessage] = React.useState<StudyGroupMessage | null>(null);
  const [inviteCopied, setInviteCopied] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [memberActionBusy, setMemberActionBusy] = React.useState<string | null>(null);
  const [usernameDraft, setUsernameDraft] = React.useState("");
  const [usernameError, setUsernameError] = React.useState<string | null>(null);
  const readMarkingRef = React.useRef(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const typingSentAt = React.useRef(0);

  const messages = [...(messagesQuery.data?.items ?? [])].reverse();
  const memberItems = membersQuery.data?.items;
  const members = React.useMemo(() => memberItems ?? [], [memberItems]);
  const memberById = React.useMemo(
    () => new Map(members.map((member) => [member.user_id, member])),
    [members],
  );

  const mentionOptions = React.useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.trim();
    const ql = q.toLowerCase();
    const filtered = members.filter(
      (m) => m.user_id !== user?.userId && memberMatchesMentionQuery(m, ql),
    );
    const allOption: StudyGroupMember = {
      user_id: STUDY_GROUP_MENTION_ALL,
      display_name: "all",
      chat_username: null,
      avatar_url: null,
      is_admin: false,
    };
    const showAll = !ql || "all".includes(ql);
    if (showAll) return [allOption, ...filtered];
    return filtered;
  }, [mentionQuery, members, user?.userId]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const hasProcessing = messages.some(
    (m) => m.delivery_status === "processing" && !m.id.startsWith("optimistic-") && !m.is_deleted,
  );

  React.useEffect(() => {
    if (!hasProcessing || !chatReady) return;
    const timer = window.setInterval(() => {
      void messagesQuery.refetch();
    }, 2500);
    return () => window.clearInterval(timer);
  }, [hasProcessing, chatReady, messagesQuery]);

  React.useEffect(() => {
    if (!user?.userId || !messagesQuery.data?.items?.length) return;
    if (readMarkingRef.current) return;
    readMarkingRef.current = true;
    void markDeliveredMessagesRead(group.id, messagesQuery.data.items, user.userId)
      .then((updated) => {
        if (updated.length === 0) return;
        patchStudyGroupMessagesCache(qc, group.id, (prev) => {
          const byId = new Map(updated.map((m) => [m.id, m]));
          return {
            ...prev,
            items: prev.items.map((m) => byId.get(m.id) ?? m),
          };
        });
      })
      .finally(() => {
        readMarkingRef.current = false;
      });
  }, [group.id, messagesQuery.data?.items, user?.userId, qc]);

  const typingLabel = React.useMemo(() => {
    const names = Object.values(typingUsers).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length === 1) return s.oneTyping(names[0]!);
    return s.manyTyping(names.slice(0, 2).join(", "));
  }, [typingUsers, s]);

  const canModerate = Boolean(group.is_admin || group.is_moderator);
  const isAdmin = Boolean(group.is_admin);

  function canRemoveMember(member: StudyGroupMember): boolean {
    if (!canModerate || member.user_id === user?.userId) return false;
    if (member.user_id === group.created_by) return false;
    if (member.is_admin && !group.is_admin) return false;
    return true;
  }

  function canBanMember(member: StudyGroupMember): boolean {
    return isAdmin && member.user_id !== user?.userId && member.user_id !== group.created_by;
  }

  async function removeMember(userId: string) {
    setMemberActionBusy(userId);
    try {
      await removeMut.mutateAsync({ groupId: group.id, userId });
      setStatsMember(null);
    } finally {
      setMemberActionBusy(null);
    }
  }

  async function banMember(userId: string) {
    setMemberActionBusy(userId);
    try {
      await banMut.mutateAsync({ groupId: group.id, userId });
      setStatsMember(null);
    } finally {
      setMemberActionBusy(null);
    }
  }

  async function deleteMessage(msg: StudyGroupMessage) {
    if (msg.id.startsWith("optimistic-")) {
      patchStudyGroupMessagesCache(qc, group.id, (prev) => ({
        ...prev,
        items: prev.items.filter((m) => m.id !== msg.id),
        total: Math.max(0, prev.total - 1),
      }));
      return;
    }
    await deleteMut.mutateAsync({ groupId: group.id, messageId: msg.id });
  }

  const shellClass =
    layout === "fullscreen"
      ? "bg-background fixed inset-0 z-50 flex flex-col"
      : "bg-background flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-[var(--shadow-card)]";

  async function saveChatUsername() {
    setUsernameError(null);
    const value = usernameDraft.trim();
    if (value.length < 3) {
      setUsernameError(s.usernameTooShort);
      return;
    }
    try {
      await setProfileMut.mutateAsync({ groupId: group.id, chat_username: value });
    } catch (err) {
      setUsernameError(apiErrorMessage(err, locale, s, s.usernameSaveFailed));
    }
  }

  if (profileQuery.isLoading) {
    return (
      <div className={cn(shellClass, "grid place-items-center")}>
        <Loader2 className="text-muted-foreground size-6 animate-spin" aria-label={s.loadingChat} />
      </div>
    );
  }

  if (profileQuery.isError) {
    return (
      <div className={cn(shellClass, "flex flex-col")}>
        <header className="border-border/60 flex shrink-0 items-center gap-3 border-b px-3 py-3">
          {layout === "fullscreen" && (
            <button
              type="button"
              aria-label={s.backToGroupsAria}
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground grid size-9 place-items-center rounded-full transition-colors active:scale-95"
            >
              <ArrowLeft className="size-5" />
            </button>
          )}
          <p className="min-w-0 flex-1 truncate font-semibold">{group.name}</p>
        </header>
        <div className="flex flex-1 flex-col justify-center px-4 py-8">
          <div className="mx-auto w-full max-w-sm space-y-4 text-center">
            <div role="alert" className="space-y-1">
              <p className="font-semibold">{s.chatProfileLoadFailedTitle}</p>
              <p className="text-muted-foreground text-sm">{s.chatProfileLoadFailedBody}</p>
            </div>
            <Button type="button" variant="outline" onClick={() => void profileQuery.refetch()}>
              {s.homeRetry}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!chatReady) {
    return (
      <div className={cn(shellClass, "flex flex-col")}>
        <header className="border-border/60 flex shrink-0 items-center gap-3 border-b px-3 py-3">
          {layout === "fullscreen" && (
            <button
              type="button"
              aria-label={s.backToGroupsAria}
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground grid size-9 place-items-center rounded-full transition-colors active:scale-95"
            >
              <ArrowLeft className="size-5" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{group.name}</p>
            <p className="text-muted-foreground text-xs">{s.pickUsernameBody}</p>
          </div>
        </header>
        <div className="flex flex-1 flex-col justify-center px-4 py-8">
          <div className="mx-auto w-full max-w-sm space-y-4">
            <div className="space-y-1 text-center">
              <p className="font-semibold">{s.pickUsernameTitle}</p>
              <p className="text-muted-foreground text-sm">{s.pickUsernameBody}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sg-chat-username">{s.chatUsernameLabel}</Label>
              <Input
                id="sg-chat-username"
                value={usernameDraft}
                autoComplete="off"
                maxLength={24}
                placeholder={s.chatUsernamePlaceholder}
                onChange={(e) => setUsernameDraft(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveChatUsername();
                }}
              />
            </div>
            {usernameError && (
              <p role="alert" className="text-destructive text-xs font-medium">
                {usernameError}
              </p>
            )}
            <Button
              className="w-full"
              disabled={setProfileMut.isPending || usernameDraft.trim().length < 3}
              onClick={() => void saveChatUsername()}
            >
              {setProfileMut.isPending ? s.saving : s.joinChat}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const displayName = chatUsername ?? user?.name ?? s.youLabel;

  function emitTyping() {
    const now = Date.now();
    if (now - typingSentAt.current < 2000) return;
    typingSentAt.current = now;
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "typing.start" }));
    }
  }

  function onDraftChange(value: string) {
    setDraft(value);
    const match = value.match(/@([\w\s.-]*)$/);
    setMentionQuery(match ? (match[1] ?? "") : null);
    setMentionIndex(0);
    if (match) emitTyping();
  }

  function pickMention(member: StudyGroupMember) {
    const label =
      member.user_id === STUDY_GROUP_MENTION_ALL
        ? "all"
        : (member.chat_username ?? member.display_name);
    const base = draft.replace(/@([\w\s.-]*)$/, `@${label} `);
    setDraft(base);
    setMentionIds((prev) => (prev.includes(member.user_id) ? prev : [...prev, member.user_id]));
    setMentionQuery(null);
    textareaRef.current?.focus();
  }

  async function copyInviteLink() {
    const url = `${window.location.origin}${window.location.pathname}?tab=groups&invite=${group.invite_code}`;
    try {
      await navigator.clipboard.writeText(url);
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      setError(s.copyInviteFailed);
    }
  }

  function pushOptimistic(partial: StudyGroupMessage) {
    patchStudyGroupMessagesCache(qc, group.id, (prev) => ({
      items: [partial, ...prev.items],
      total: prev.total + 1,
    }));
  }

  async function send() {
    if (group.archived_at) return;
    setError(null);
    const text = draft.trim();
    if (!text || !user) return;

    const mentions = [...mentionIds];
    const clientMessageId = newClientMessageId();
    const optimistic: StudyGroupMessage = {
      id: `optimistic-${clientMessageId}`,
      group_id: group.id,
      author_user_id: user.userId,
      author_name: displayName,
      kind: "text",
      body: text,
      image_url: null,
      mention_user_ids: mentions,
      client_message_id: clientMessageId,
      delivery_status: "processing",
      failure_reason: null,
      read_by: [],
      created_at: new Date().toISOString(),
    };
    pushOptimistic(optimistic);
    setDraft("");
    setMentionIds([]);
    setMentionQuery(null);

    try {
      const saved = await postMut.mutateAsync({
        groupId: group.id,
        body: text,
        mention_user_ids: mentions,
        client_message_id: clientMessageId,
      });
      mergeStudyGroupMessageInCache(qc, group.id, saved);
    } catch (err) {
      patchStudyGroupMessagesCache(qc, group.id, (prev) => ({
        ...prev,
        items: prev.items.map((m) =>
          m.client_message_id === clientMessageId
            ? { ...m, delivery_status: "failed", failure_reason: "network" }
            : m,
        ),
      }));
      setError(apiErrorMessage(err, locale, s, s.sendMessageFailed));
    }
  }

  async function onImagePick(file: File) {
    setError(null);
    setUploading(true);
    if (!user) return;

    const clientMessageId = newClientMessageId();
    try {
      const signed = await coachApi.mintStudyGroupImageUpload(group.id, {
        content_type: file.type || "image/png",
        file_size: file.size,
      });
      await putFileToSignedUrl(signed, file);

      pushOptimistic({
        id: `optimistic-${clientMessageId}`,
        group_id: group.id,
        author_user_id: user.userId,
        author_name: displayName,
        kind: "image",
        body: "",
        image_url: signed.public_url,
        mention_user_ids: mentionIds,
        client_message_id: clientMessageId,
        delivery_status: "processing",
        failure_reason: null,
        read_by: [],
        created_at: new Date().toISOString(),
      });

      const saved = await postImageMut.mutateAsync({
        groupId: group.id,
        image_url: signed.public_url,
        caption: "",
        mention_user_ids: mentionIds,
        client_message_id: clientMessageId,
      });
      mergeStudyGroupMessageInCache(qc, group.id, saved);
      setMentionIds([]);
    } catch (err) {
      setError(apiErrorMessage(err, locale, s, s.sendImageFailed));
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <div className={shellClass}>
        <header className="border-border/60 bg-background/95 flex shrink-0 items-center gap-3 border-b px-3 py-3 backdrop-blur-md">
          {layout === "embedded" && listCollapsed && onToggleList && (
            <button
              type="button"
              aria-label={s.showGroupListAria}
              onClick={onToggleList}
              className="text-muted-foreground hover:text-foreground grid size-9 place-items-center rounded-full transition-colors active:scale-95"
            >
              <PanelLeft className="size-5" />
            </button>
          )}
          <button
            type="button"
            aria-label={s.backToGroupsAria}
            onClick={onClose}
            className={cn(
              "text-muted-foreground hover:text-foreground grid size-9 place-items-center rounded-full transition-colors active:scale-95",
              layout === "embedded" && "hidden",
            )}
          >
            <ArrowLeft className="size-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{group.name}</p>
            <p className="text-muted-foreground truncate text-xs tabular-nums">
              {s.membersCountPlural(group.member_count)}
              {!connected && s.reconnecting}
            </p>
          </div>
          <button
            type="button"
            aria-label={isAdmin ? s.inviteLinkTitle : inviteCopied ? s.copied : s.copy}
            title={`${isAdmin ? s.inviteLinkTitle : s.copy} (${s.studyCentreMembersOnly})`}
            onClick={() => {
              if (isAdmin) {
                setInviteOpen(true);
                return;
              }
              void copyInviteLink();
            }}
            className="text-muted-foreground hover:text-foreground grid size-9 place-items-center rounded-full transition-colors active:scale-95"
          >
            {inviteCopied && !isAdmin ? (
              <Check className="text-primary size-5" />
            ) : (
              <Link2 className="size-5" />
            )}
          </button>
          <button
            type="button"
            aria-label={s.viewMembersAria}
            onClick={() => setMembersOpen(true)}
            className="text-muted-foreground hover:text-foreground grid size-9 place-items-center rounded-full transition-colors active:scale-95"
          >
            <Users className="size-5" />
          </button>
        </header>

        <div
          ref={scrollRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-4"
          aria-live="polite"
        >
          {messagesQuery.isLoading ? (
            <Loader2 className="text-muted-foreground mx-auto size-5 animate-spin" />
          ) : messagesQuery.isError ? (
            <div className="mx-auto flex max-w-sm flex-col items-center gap-3 py-8 text-center">
              <div role="alert" className="space-y-1">
                <p className="font-semibold">{s.messagesLoadFailedTitle}</p>
                <p className="text-muted-foreground text-sm">{s.messagesLoadFailedBody}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void messagesQuery.refetch()}
              >
                {s.homeRetry}
              </Button>
            </div>
          ) : messages.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">{s.noMessagesYet}</p>
          ) : (
            collapseDeletedRuns(messages).map((entry) =>
              entry.kind === "deletedRun" ? (
                <DeletedRunNote key={entry.key} count={entry.count} by={entry.by} />
              ) : (
                <ChatBubble
                  key={entry.msg.client_message_id ?? entry.msg.id}
                  msg={entry.msg}
                  self={entry.msg.author_user_id === user?.userId}
                  authorAvatarUrl={memberById.get(entry.msg.author_user_id)?.avatar_url ?? null}
                  memberCount={group.member_count}
                  canDelete={
                    !entry.msg.is_deleted &&
                    (entry.msg.author_user_id === user?.userId ||
                      (canModerate && entry.msg.author_user_id !== user?.userId))
                  }
                  onImageOpen={setLightboxUrl}
                  onReadBy={setReadByMessage}
                  onDelete={() => void deleteMessage(entry.msg)}
                />
              ),
            )
          )}
        </div>

        <footer
          className={cn(
            "border-border/60 bg-background relative shrink-0 border-t px-3 py-3",
            layout === "fullscreen" ? "pb-24" : "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          )}
        >
          {group.archived_at ? (
            <p className="text-muted-foreground mb-2 text-xs">{s.archivedReadOnly}</p>
          ) : null}
          {typingLabel && (
            <p className="text-muted-foreground mb-2 text-xs font-medium">{typingLabel}</p>
          )}
          {mentionQuery !== null && mentionOptions.length > 0 && (
            <ul
              role="listbox"
              aria-label={s.mentionMemberAria}
              className="bg-popover border-border/60 absolute bottom-full left-3 right-3 mb-2 max-h-44 overflow-y-auto rounded-xl border shadow-md"
            >
              {mentionOptions.map((m, idx) => (
                <li key={m.user_id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={idx === mentionIndex}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                      idx === mentionIndex ? "bg-muted/70" : "hover:bg-muted/60",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickMention(m);
                    }}
                  >
                    {m.user_id === STUDY_GROUP_MENTION_ALL ? (
                      <span className="bg-primary/15 text-primary grid size-7 place-items-center rounded-full text-xs font-bold">
                        @
                      </span>
                    ) : (
                      <MemberAvatar name={m.display_name} url={m.avatar_url} size="sm" />
                    )}
                    <span className="min-w-0 flex-1">
                      {m.user_id === STUDY_GROUP_MENTION_ALL ? (
                        <span className="block truncate font-medium">
                          @<HighlightMatch text="all" query={mentionQuery} />
                        </span>
                      ) : (
                        <MemberIdentity member={m} nameClassName="text-sm font-bold" />
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p role="alert" className="text-destructive mb-2 text-xs font-medium">
              {error}
            </p>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onImagePick(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              aria-label={s.attachImageAria}
              disabled={
                Boolean(group.archived_at) ||
                messagesQuery.isError ||
                uploading ||
                postImageMut.isPending
              }
              onClick={() => fileRef.current?.click()}
              className="text-muted-foreground hover:text-foreground grid size-10 shrink-0 place-items-center rounded-full transition-colors active:scale-95 disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ImagePlus className="size-5" />
              )}
            </button>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              placeholder={group.archived_at ? s.archivedReadOnly : s.messagePlaceholder}
              rows={1}
              disabled={Boolean(group.archived_at) || messagesQuery.isError}
              className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring max-h-28 min-h-10 flex-1 resize-none rounded-2xl border px-4 py-2.5 text-sm leading-snug focus-visible:outline-none focus-visible:ring-2"
              onKeyDown={(e) => {
                if (mentionQuery !== null && mentionOptions.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setMentionIndex((i) => Math.min(i + 1, mentionOptions.length - 1));
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setMentionIndex((i) => Math.max(i - 1, 0));
                    return;
                  }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    pickMention(mentionOptions[mentionIndex]!);
                    return;
                  }
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Button
              type="button"
              size="icon"
              className="size-10 shrink-0 rounded-full"
              disabled={Boolean(group.archived_at) || messagesQuery.isError || !draft.trim()}
              aria-label={s.sendMessageAria}
              onClick={() => void send()}
            >
              <Send className="size-4" />
            </Button>
          </div>
        </footer>
      </div>

      {lightboxUrl && <MediaLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      {readByMessage && (
        <ReceiptsSheet
          groupId={group.id}
          message={readByMessage}
          onClose={() => setReadByMessage(null)}
        />
      )}

      <Sheet open={membersOpen} onOpenChange={setMembersOpen}>
        <ResponsiveSheetContent>
          <SheetHeader>
            <SheetTitle>{s.membersTitle}</SheetTitle>
            <SheetDescription>{s.membersDesc}</SheetDescription>
          </SheetHeader>
          {membersQuery.isLoading && (
            <Loader2
              className="text-muted-foreground mx-auto mt-6 size-5 animate-spin"
              aria-label={s.membersTitle}
            />
          )}
          {membersQuery.isError && (
            <div className="mt-6 space-y-3 text-center">
              <p role="alert" className="text-muted-foreground text-sm">
                {s.membersLoadFailed}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void membersQuery.refetch()}
              >
                {s.homeRetry}
              </Button>
            </div>
          )}
          <ul
            className={cn(
              "mt-4 space-y-1",
              (membersQuery.isLoading || membersQuery.isError) && "hidden",
            )}
          >
            {members.map((m) => {
              const showActions = canRemoveMember(m) || canBanMember(m);
              return (
                <li key={m.user_id} className="flex items-center gap-1">
                  <button
                    type="button"
                    className="hover:bg-muted/50 flex min-w-0 flex-1 items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors"
                    onClick={() => {
                      setStatsMember(m);
                      setMembersOpen(false);
                    }}
                  >
                    <MemberAvatar name={m.display_name} url={m.avatar_url} />
                    <span className="min-w-0 flex-1">
                      <MemberIdentity member={m} />
                      {m.is_admin && (
                        <span className="text-muted-foreground text-xs">{s.groupAdmin}</span>
                      )}
                      {m.is_moderator && !m.is_admin && (
                        <span className="text-muted-foreground text-xs">{s.moderator}</span>
                      )}
                    </span>
                  </button>
                  {showActions && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={s.memberActionsAria(m.display_name)}
                          disabled={memberActionBusy === m.user_id}
                          className="text-muted-foreground hover:text-foreground grid size-9 shrink-0 place-items-center rounded-full transition-colors disabled:opacity-50"
                        >
                          {memberActionBusy === m.user_id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <MoreVertical className="size-4" />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canRemoveMember(m) && (
                          <DropdownMenuItem onClick={() => void removeMember(m.user_id)}>
                            <UserMinus className="mr-2 size-4" />
                            {s.removeFromGroup}
                          </DropdownMenuItem>
                        )}
                        {canBanMember(m) && (
                          <>
                            {canRemoveMember(m) && <DropdownMenuSeparator />}
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => void banMember(m.user_id)}
                            >
                              <ShieldBan className="mr-2 size-4" />
                              {s.banFromGroup}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </li>
              );
            })}
          </ul>
        </ResponsiveSheetContent>
      </Sheet>

      {statsMember && (
        <MemberStatsSheet
          group={group}
          member={statsMember}
          onClose={() => setStatsMember(null)}
          canRemove={canRemoveMember(statsMember)}
          canBan={canBanMember(statsMember)}
          busy={memberActionBusy === statsMember.user_id}
          onRemove={async (userId) => {
            await removeMember(userId);
          }}
          onBan={async (userId) => {
            await banMember(userId);
          }}
          onPromoteModerator={async (userId) => {
            await promoteMut.mutateAsync({ groupId: group.id, userId });
          }}
          onDemoteModerator={async (userId) => {
            await demoteMut.mutateAsync({ groupId: group.id, userId });
          }}
        />
      )}

      {isAdmin && (
        <InviteSettingsSheet
          group={group}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          inviteCopied={inviteCopied}
          onCopy={() => void copyInviteLink()}
          onSave={async (mode, regenerate) => {
            await inviteSettingsMut.mutateAsync({
              groupId: group.id,
              invite_link_mode: mode,
              regenerate_code: regenerate,
            });
          }}
          saving={inviteSettingsMut.isPending}
        />
      )}
    </>
  );
}

function MediaLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  const { strings: s } = usePortalLocale();
  return (
    <div
      className="bg-background/95 fixed inset-0 z-[60] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={s.imagePreviewAria}
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <button
          type="button"
          aria-label={s.closeAria}
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground grid size-10 place-items-center rounded-full"
        >
          <X className="size-5" />
        </button>
        <a
          href={url}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary inline-flex items-center gap-1.5 text-sm font-semibold"
        >
          <Download className="size-4" aria-hidden="true" />
          {s.download}
        </a>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
        <img src={url} alt="" className="max-h-full max-w-full touch-pinch-zoom object-contain" />
      </div>
    </div>
  );
}

function ReceiptsSheet({
  groupId,
  message,
  onClose,
}: {
  groupId: string;
  message: StudyGroupMessage;
  onClose: () => void;
}) {
  const { locale, strings: s } = usePortalLocale();
  const receiptsQuery = useStudyGroupMessageReceipts(groupId, message.id);

  const grouped = React.useMemo(() => {
    const items = receiptsQuery.data?.items ?? [];
    return {
      seen: items.filter((e) => e.status === "seen"),
      delivered: items.filter((e) => e.status === "delivered"),
      pending: items.filter((e) => e.status === "pending"),
      not_delivered: items.filter((e) => e.status === "not_delivered"),
    };
  }, [receiptsQuery.data?.items]);

  function section(title: string, entries: typeof grouped.seen) {
    if (entries.length === 0) return null;
    return (
      <div className="space-y-2">
        <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          {title}
        </p>
        <ul className="space-y-2">
          {entries.map((entry) => (
            <li key={entry.user_id} className="flex items-center justify-between text-sm">
              <span className="font-medium">{entry.display_name}</span>
              {entry.read_at ? (
                <span className="text-muted-foreground text-xs tabular-nums">
                  {formatTime(entry.read_at, locale)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <ResponsiveSheetContent className="max-h-[60vh]">
        <SheetHeader>
          <SheetTitle>{s.messageInfo}</SheetTitle>
          <SheetDescription>{s.messageInfoDesc}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {receiptsQuery.isLoading ? (
            <Loader2 className="text-muted-foreground mx-auto size-5 animate-spin" />
          ) : receiptsQuery.isError ? (
            <div className="space-y-3 text-center">
              <p role="alert" className="text-muted-foreground text-sm">
                {s.receiptsLoadFailed}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void receiptsQuery.refetch()}
              >
                {s.homeRetry}
              </Button>
            </div>
          ) : (
            <>
              {section(s.receiptSeen, grouped.seen)}
              {section(s.receiptDelivered, grouped.delivered)}
              {section(s.receiptPending, grouped.pending)}
              {section(s.receiptNotDelivered, grouped.not_delivered)}
              {receiptsQuery.data?.total === 0 && (
                <p className="text-muted-foreground text-sm">{s.receiptsEmpty}</p>
              )}
            </>
          )}
        </div>
      </ResponsiveSheetContent>
    </Sheet>
  );
}

function MessageReceipt({
  msg,
  self,
  memberCount,
  onReadBy,
}: {
  msg: StudyGroupMessage;
  self: boolean;
  memberCount: number;
  onReadBy: () => void;
}) {
  const { strings: s } = usePortalLocale();
  const status = msg.delivery_status ?? "delivered";
  const isOptimistic = msg.id.startsWith("optimistic-");
  const bodyHasUrl = Boolean(msg.body && /https?:\/\//i.test(msg.body));

  if (isOptimistic && !bodyHasUrl && self) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-0.5 opacity-80"
        aria-label={s.viewReceiptsAria}
        onClick={onReadBy}
      >
        <Check className="size-3.5" aria-hidden="true" />
      </button>
    );
  }

  if (status === "processing") {
    if (isOptimistic && bodyHasUrl) {
      return (
        <span className="text-[10px] font-medium opacity-90" aria-live="polite">
          {s.checkingLinks}
        </span>
      );
    }
    if (!isOptimistic) {
      return (
        <Loader2 className="inline size-3 animate-spin opacity-80" aria-label={s.processingAria} />
      );
    }
  }
  if (status === "failed") {
    const code = msg.failure_reason;
    const label =
      code === "profanity"
        ? s.failureProfanity
        : code === "malicious_link"
          ? s.failureMaliciousLink
          : code === "nsfw_image"
            ? s.failureNsfwImage
            : code === "invalid_link"
              ? s.failureInvalidLink
              : s.messageCouldNotDeliver;
    return (
      <span
        className={cn(
          "text-destructive text-[10px] font-medium",
          self && "bg-background/95 rounded-full px-1.5 py-px",
        )}
      >
        {label}
      </span>
    );
  }
  if (!self) return null;

  const readers = (msg.read_by ?? []).length;
  const others = Math.max(memberCount - 1, 1);
  const allRead = readers >= others;
  const Icon = readers > 0 ? CheckCheck : Check;
  const color = allRead ? "text-sky-300" : "opacity-80";

  return (
    <button
      type="button"
      className={cn("inline-flex items-center gap-0.5", color)}
      aria-label={s.viewReceiptsAria}
      onClick={onReadBy}
    >
      <Icon className="size-3.5" aria-hidden="true" />
    </button>
  );
}

function InviteSettingsSheet({
  group,
  open,
  onOpenChange,
  inviteCopied,
  onCopy,
  onSave,
  saving,
}: {
  group: StudyGroup;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inviteCopied: boolean;
  onCopy: () => void;
  onSave: (mode: "permanent" | "single_use", regenerate: boolean) => Promise<void>;
  saving: boolean;
}) {
  const { strings: s } = usePortalLocale();
  const [mode, setMode] = React.useState<"permanent" | "single_use">(
    group.invite_link_mode ?? "permanent",
  );

  React.useEffect(() => {
    if (open) setMode(group.invite_link_mode ?? "permanent");
  }, [open, group.invite_link_mode]);

  const inviteUrl = `${window.location.origin}${window.location.pathname}?tab=groups&invite=${group.invite_code}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <ResponsiveSheetContent>
        <SheetHeader>
          <SheetTitle>{s.inviteLinkTitle}</SheetTitle>
          <SheetDescription>{s.inviteLinkDesc}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label>{s.linkTypeLabel}</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as "permanent" | "single_use")}
              className="space-y-2"
            >
              <label className="border-border/60 hover:bg-muted/40 flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors">
                <RadioGroupItem value="permanent" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-semibold">{s.linkTypePermanent}</span>
                  <span className="text-muted-foreground text-xs">{s.linkTypePermanentDesc}</span>
                </span>
              </label>
              <label className="border-border/60 hover:bg-muted/40 flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors">
                <RadioGroupItem value="single_use" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-semibold">{s.linkTypeOneTime}</span>
                  <span className="text-muted-foreground text-xs">{s.linkTypeOneTimeDesc}</span>
                </span>
              </label>
            </RadioGroup>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sg-invite-url">{s.inviteLinkTitle}</Label>
            <div className="flex gap-2">
              <Input id="sg-invite-url" readOnly value={inviteUrl} className="text-xs" />
              <Button type="button" variant="outline" onClick={onCopy}>
                {inviteCopied ? s.copied : s.copy}
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="flex-1"
              disabled={saving || mode === (group.invite_link_mode ?? "permanent")}
              onClick={() => void onSave(mode, false)}
            >
              {saving ? s.saving : s.savePreference}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={saving}
              onClick={() => void onSave(mode, true)}
            >
              {s.regenerateLink}
            </Button>
          </div>
        </div>
      </ResponsiveSheetContent>
    </Sheet>
  );
}

function MemberStatsSheet({
  group,
  member,
  onClose,
  canRemove,
  canBan,
  busy,
  onRemove,
  onBan,
  onPromoteModerator,
  onDemoteModerator,
}: {
  group: StudyGroup;
  member: StudyGroupMember;
  onClose: () => void;
  canRemove: boolean;
  canBan: boolean;
  busy: boolean;
  onRemove: (userId: string) => Promise<void>;
  onBan: (userId: string) => Promise<void>;
  onPromoteModerator: (userId: string) => Promise<void>;
  onDemoteModerator: (userId: string) => Promise<void>;
}) {
  const { user } = useSession();
  const { strings: s } = usePortalLocale();
  const statsQuery = useStudyGroupMemberStats(group.id, member.user_id);
  const [modBusy, setModBusy] = React.useState(false);
  const canPromote =
    group.is_admin && !member.is_admin && !member.is_moderator && member.user_id !== user?.userId;
  const canDemote = group.is_admin && member.is_moderator && member.user_id !== user?.userId;
  const plans = statsQuery.data?.plan_summaries ?? [];

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <ResponsiveSheetContent>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <MemberAvatar name={member.display_name} url={member.avatar_url} size="lg" />
            <MemberIdentity member={member} nameClassName="text-lg font-bold" />
          </SheetTitle>
          <SheetDescription>{s.memberStatsDesc}</SheetDescription>
        </SheetHeader>
        {statsQuery.isLoading ? (
          <Loader2 className="text-muted-foreground mx-auto mt-6 size-5 animate-spin" />
        ) : statsQuery.data ? (
          <>
            <dl className="mt-6 grid grid-cols-2 gap-3">
              <Stat label={s.statDueNow} value={statsQuery.data.due_now} />
              <Stat label={s.statReviewedToday} value={statsQuery.data.reviewed_today} />
              <Stat label={s.statDayStreak} value={statsQuery.data.streak_days} />
              <Stat label={s.statActivePlans} value={statsQuery.data.active_plans} />
              <Stat
                label={s.statTotalCards}
                value={statsQuery.data.cards_total}
                className="col-span-2"
              />
            </dl>
            {plans.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide">{s.statActivePlans}</p>
                <ul className="space-y-2">
                  {plans.map((plan) => (
                    <li key={plan.id} className="bg-muted/40 rounded-xl px-3 py-2.5 text-sm">
                      <p className="font-medium">{plan.title}</p>
                      {plan.subject && (
                        <p className="text-muted-foreground text-xs">{plan.subject}</p>
                      )}
                    </li>
                  ))}
                </ul>
                {statsQuery.data.active_plans > plans.length && (
                  <p className="text-muted-foreground text-xs">
                    {s.morePlans(statsQuery.data.active_plans - plans.length)}
                  </p>
                )}
              </div>
            )}
          </>
        ) : null}
        {canPromote && (
          <Button
            type="button"
            variant="outline"
            className="mt-6 w-full"
            disabled={modBusy}
            onClick={() => {
              setModBusy(true);
              void onPromoteModerator(member.user_id).finally(() => setModBusy(false));
            }}
          >
            {modBusy ? s.updating : s.makeModerator}
          </Button>
        )}
        {canDemote && (
          <Button
            type="button"
            variant="outline"
            className="mt-3 w-full"
            disabled={modBusy}
            onClick={() => {
              setModBusy(true);
              void onDemoteModerator(member.user_id).finally(() => setModBusy(false));
            }}
          >
            {modBusy ? s.updating : s.removeModerator}
          </Button>
        )}
        {canRemove && (
          <Button
            type="button"
            variant="outline"
            className="mt-6 w-full"
            disabled={busy}
            onClick={() => void onRemove(member.user_id)}
          >
            {busy ? s.removing : s.removeFromGroup}
          </Button>
        )}
        {canBan && (
          <Button
            type="button"
            variant="destructive"
            className="mt-3 w-full"
            disabled={busy}
            onClick={() => void onBan(member.user_id)}
          >
            {busy ? s.banning : s.banFromGroup}
          </Button>
        )}
      </ResponsiveSheetContent>
    </Sheet>
  );
}

function Stat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className={cn("bg-muted/40 rounded-xl px-3 py-2.5", className)}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="font-display text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function MemberAvatar({
  name,
  url,
  size = "md",
}: {
  name: string;
  url: string | null;
  size?: "sm" | "md" | "lg";
}) {
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");
  const dim =
    size === "sm" ? "size-7 text-[10px]" : size === "lg" ? "size-14 text-lg" : "size-9 text-xs";
  if (url) {
    return (
      <img
        src={url}
        alt={`${name}'s profile photo`}
        className={cn("shrink-0 rounded-full object-cover", dim)}
      />
    );
  }
  return (
    <span
      className={cn(
        "from-primary grid shrink-0 place-items-center rounded-full bg-gradient-to-br to-[color-mix(in_oklch,var(--color-primary)_55%,oklch(0.66_0.2_340))] font-semibold text-white",
        dim,
      )}
    >
      {initials}
    </span>
  );
}

function hostFromHttpUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.host;
  } catch {
    return "";
  }
}

/** Defence in depth: legacy messages can carry previews approved before the server rejected these. */
function isNonPublicHost(host: string): boolean {
  let name: string;
  try {
    name = new URL(`http://${host}`).hostname.replace(/^\[|\]$/g, "");
  } catch {
    return true;
  }
  if (name === "localhost" || name.endsWith(".localhost") || name.endsWith(".local")) return true;
  if (name.includes(":")) return name === "::1" || /^(f[cd]|fe80)/.test(name);
  const octets = name.split(".").map(Number);
  if (octets.length !== 4 || octets.some((o) => !Number.isInteger(o) || o < 0 || o > 255))
    return false;
  const [a, b] = octets as [number, number, number, number];
  return (
    a === 0 ||
    a === 10 ||
    a === 127 ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168)
  );
}

function approvedLinkPreviews(
  previews: StudyGroupMessage["link_previews"],
): { url: string; host: string }[] {
  if (!previews?.length) return [];
  const items: { url: string; host: string }[] = [];
  for (const preview of previews) {
    const url = preview.url.trim();
    if (!/^https?:\/\//i.test(url)) continue;
    const host = preview.host?.trim() || hostFromHttpUrl(url);
    if (!host || isNonPublicHost(host) || isNonPublicHost(hostFromHttpUrl(url))) continue;
    items.push({ url, host });
  }
  return items;
}

function VerifiedLinkPreviews({
  previews,
  self,
}: {
  previews: StudyGroupMessage["link_previews"];
  self: boolean;
}) {
  const { strings: s } = usePortalLocale();
  const items = approvedLinkPreviews(previews);
  if (items.length === 0) return null;

  return (
    <ul className="mt-1.5 flex min-w-0 max-w-full list-none flex-col gap-1">
      {items.map((item) => (
        <li key={item.url} className="min-w-0 max-w-full">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.verifiedLinkAria(item.host)}
            className={cn(
              "focus-visible:ring-ring flex min-w-0 max-w-full items-start gap-1.5 break-all rounded-lg border px-2 py-1 text-xs font-medium leading-snug focus-visible:outline-none focus-visible:ring-2",
              self
                ? "border-primary-foreground/25 bg-primary-foreground/10 text-primary-foreground"
                : "border-border bg-background text-foreground",
            )}
          >
            <ExternalLink className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
            <span className="min-w-0 break-all">{item.host}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

type ChatEntry =
  | { kind: "message"; msg: StudyGroupMessage }
  | { kind: "deletedRun"; key: string; count: number; by: string | null };

function collapseDeletedRuns(messages: StudyGroupMessage[]): ChatEntry[] {
  const entries: ChatEntry[] = [];
  let run: StudyGroupMessage[] = [];
  const flush = () => {
    if (run.length === 1) {
      entries.push({ kind: "message", msg: run[0]! });
    } else if (run.length > 1) {
      const names = new Set(run.map((m) => m.deleted_by_name ?? null));
      const [only] = names;
      entries.push({
        kind: "deletedRun",
        key: `deleted-${run[0]!.id}`,
        count: run.length,
        by: names.size === 1 ? (only ?? null) : null,
      });
    }
    run = [];
  };
  for (const msg of messages) {
    if (msg.is_deleted) {
      run.push(msg);
    } else {
      flush();
      entries.push({ kind: "message", msg });
    }
  }
  flush();
  return entries;
}

function DeletedRunNote({ count, by }: { count: number; by: string | null }) {
  const { strings: s } = usePortalLocale();
  return (
    <div className="flex justify-center">
      <p className="text-muted-foreground rounded-xl border border-dashed px-3 py-2 text-center text-xs italic">
        {s.messagesDeletedRun(count, by)}
      </p>
    </div>
  );
}

function ChatBubble({
  msg,
  self,
  authorAvatarUrl,
  memberCount,
  canDelete,
  onImageOpen,
  onReadBy,
  onDelete,
}: {
  msg: StudyGroupMessage;
  self: boolean;
  authorAvatarUrl: string | null;
  memberCount: number;
  canDelete: boolean;
  onImageOpen: (url: string) => void;
  onReadBy: (msg: StudyGroupMessage) => void;
  onDelete: () => void;
}) {
  const { strings: s, locale } = usePortalLocale();
  const failed = msg.delivery_status === "failed";
  const deleted = Boolean(msg.is_deleted);

  if (deleted) {
    return (
      <div className={cn("flex", self ? "justify-end" : "justify-start")}>
        <p
          className={cn(
            "text-muted-foreground max-w-[90%] rounded-xl border border-dashed px-3 py-2 text-xs italic",
            self ? "text-right" : "text-left",
          )}
        >
          {msg.deleted_by_name
            ? `${s.messageDeletedBy} ${msg.deleted_by_name}.`
            : s.messageDeletedFallback}
        </p>
      </div>
    );
  }

  return (
    <div className={cn("group flex items-end gap-1", self ? "justify-end" : "justify-start")}>
      {!self ? <MemberAvatar name={msg.author_name} url={authorAvatarUrl} size="sm" /> : null}
      {canDelete && self && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={s.messageActionsAria}
              className="text-muted-foreground hover:text-foreground mb-1 grid size-7 shrink-0 place-items-center rounded-full opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
            >
              <MoreVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={self ? "end" : "start"}>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-2 size-4" />
              {s.deleteMessage}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <div
        className={cn(
          "min-w-0 max-w-[85%] rounded-2xl px-3.5 py-2 shadow-sm",
          failed && "border-destructive/40 border",
          self
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-muted/70 text-foreground rounded-bl-md",
        )}
      >
        {!self && (
          <p className="text-primary mb-0.5 text-[11px] font-semibold leading-none">
            {msg.author_name}
          </p>
        )}
        {msg.kind === "image" && msg.image_url && (
          <button
            type="button"
            className="mb-1 block w-full overflow-hidden rounded-lg"
            onClick={() => onImageOpen(msg.image_url!)}
          >
            <img
              src={msg.image_url}
              alt={msg.body || s.sharedImageAlt}
              className="max-h-48 w-full object-cover transition-transform duration-150 hover:scale-[1.02] motion-reduce:transition-none"
              loading="lazy"
            />
          </button>
        )}
        {msg.body ? (
          <p className="whitespace-pre-wrap break-words text-sm leading-snug">{msg.body}</p>
        ) : null}
        <VerifiedLinkPreviews previews={msg.link_previews} self={self} />
        <div
          className={cn(
            "mt-1 flex items-center justify-end gap-1.5 text-[10px] tabular-nums",
            self ? "text-primary-foreground/80" : "text-muted-foreground",
          )}
        >
          <span>{formatTime(msg.created_at, locale)}</span>
          <MessageReceipt
            msg={msg}
            self={self}
            memberCount={memberCount}
            onReadBy={() => onReadBy(msg)}
          />
        </div>
      </div>
      {canDelete && !self && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={s.messageActionsAria}
              className="text-muted-foreground hover:text-foreground mb-1 grid size-7 shrink-0 place-items-center rounded-full opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
            >
              <MoreVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-2 size-4" />
              {s.deleteMessage}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
